'use client';

import { c as _c } from "react/compiler-runtime";
import { ActivityIcon, AlertTriangleIcon, ChevronDownIcon, ChevronUpIcon, InfoIcon, TriangleAlertIcon, UploadIcon, XCircleIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// Local type definitions mirroring packages/engine/src/systems/PerformanceSnapshot.ts
// (not re-exported from @gnsx/genesys.js to avoid import cycles)

type PerformanceIssueSeverity = 'info' | 'warning' | 'critical';
type PerformanceIssueCategory = 'timing' | 'render' | 'simulation' | 'physics' | 'network' | 'memory';
interface PerformanceIssue {
  id: string;
  severity: PerformanceIssueSeverity;
  category: PerformanceIssueCategory;
  message: string;
  suggestion: string;
}
interface ProfilerScopeStats {
  label: string;
  samples: number;
  avg: number;
  min: number;
  max: number;
  p95: number;
  selfAvg?: number;
  selfMin?: number;
  selfMax?: number;
  selfP95?: number;
  callsPerFrame?: number;
}
interface FrameTimingStats {
  frameCount: number;
  avgMs: number;
  minMs: number;
  maxMs: number;
  p95Ms: number;
  slowFrames: number;
}
interface GpuMemoryBucket {
  count: number;
  sizeBytes?: number;
}
interface PerformanceSnapshotGpuMemory {
  totalBytes?: number;
  attributes?: GpuMemoryBucket;
  geometries: GpuMemoryBucket;
  indexAttributes?: GpuMemoryBucket;
  indirectStorageAttributes?: GpuMemoryBucket;
  programs: GpuMemoryBucket;
  readbackBuffers?: GpuMemoryBucket;
  renderTargets?: GpuMemoryBucket;
  storageAttributes?: GpuMemoryBucket;
  textures: GpuMemoryBucket;
}
interface PerformanceSnapshotV1 {
  schemaVersion: 1;
  capturedAt: string;
  meta: {
    gameId?: string;
    mapName?: string;
    renderer: 'webgl' | 'webgpu' | 'unknown';
    playState: string;
    fps?: number;
  };
  render: {
    drawCalls: number;
    triangles: number;
    points: number;
    lines: number;
    geometries: number;
    textures: number;
    programs?: number;
  };
  world: {
    actorCount: number;
    actorsByClass: {
      className: string;
      count: number;
    }[];
  };
  memory: {
    jsHeapUsedMB?: number;
    gpu?: PerformanceSnapshotGpuMemory;
  };
  timing?: {
    profiler: {
      enabled: boolean;
      profile: string;
      sampleWindow: number;
    };
    frameStats?: FrameTimingStats;
    scopes: ProfilerScopeStats[];
    frameTimeMs?: number;
  };
  simulation?: {
    physics?: {
      bodies: number;
      colliders: number;
      vehicles: number;
      characterControllers?: number;
      collisionEventsLastFrame?: number;
    };
    timers?: {
      activeTimers: number;
    };
    tweens?: {
      activeTweens: number;
    };
    tickAudit?: {
      actorsTicking: number;
      componentsWithTickOverride: {
        className: string;
        count: number;
      }[];
    };
    idleTickerCount?: number;
  };
  network?: {
    numLocalObjects: number;
    numRemoteObjects: number;
    bytesSentPerSecond: number;
    bytesReceivedPerSecond: number;
    numClientsInSession: number;
    serverRttMs: number | null;
  };
  issues: PerformanceIssue[];
}

// ─── helpers ────────────────────────────────────────────────────────────────

function ms(value: number): string {
  return `${value.toFixed(2)} ms`;
}
function fmtNum(value: number): string {
  return value.toLocaleString();
}
function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
function formatGpuBucketSize(bucket: GpuMemoryBucket | undefined): string {
  if (!bucket) {
    return '—';
  }
  return bucket.sizeBytes !== undefined ? formatBytes(bucket.sizeBytes) : 'N/A';
}
function formatGpuMemoryTitle(memory: PerformanceSnapshotV1['memory']): string {
  const gpu = memory.gpu;
  if (gpu?.totalBytes !== undefined) {
    return `GPU Memory — ${formatBytes(gpu.totalBytes)}`;
  }
  if (memory.jsHeapUsedMB !== undefined) {
    return `GPU Memory — ${memory.jsHeapUsedMB} MB JS Heap`;
  }
  if (gpu) {
    const parts: string[] = [];
    if (gpu.textures.count > 0) {
      parts.push(`${fmtNum(gpu.textures.count)} tex`);
    }
    if (gpu.geometries.count > 0) {
      parts.push(`${fmtNum(gpu.geometries.count)} geo`);
    }
    if (gpu.programs.count > 0) {
      parts.push(`${fmtNum(gpu.programs.count)} programs`);
    }
    if (parts.length > 0) {
      return `GPU Memory — ${parts.join(', ')}`;
    }
  }
  return 'GPU Memory';
}
const SCOPES_PAGE = 50;
/** Highlight scopes whose inclusive avg exceeds 50% of a 60 fps frame budget (~8.33 ms). */
const SCOPE_SLOW_MS = 1000 / 60 * 0.5;

// ─── sub-components ─────────────────────────────────────────────────────────

function SectionCard(t0) {
  const $ = _c(5);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <h3 className="mb-3 text-sm font-semibold text-foreground">{title}</h3>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className="rounded-lg border border-border bg-card p-4">{t1}{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function CollapsibleSectionCard(t0) {
  const $ = _c(12);
  const {
    title,
    defaultOpen: t1,
    children
  } = t0;
  const defaultOpen = t1 === undefined ? true : t1;
  const [open, setOpen] = useState(defaultOpen);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setOpen(_temp);
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== open) {
    t3 = open ? <ChevronUpIcon className="size-4 text-muted-foreground" /> : <ChevronDownIcon className="size-4 text-muted-foreground" />;
    $[1] = open;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t3 || $[4] !== title) {
    t4 = <button type="button" className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-foreground hover:bg-muted/40" onClick={t2}>{title}{t3}</button>;
    $[3] = t3;
    $[4] = title;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== children || $[7] !== open) {
    t5 = open && <div className="px-4 pb-4">{children}</div>;
    $[6] = children;
    $[7] = open;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <div className="rounded-lg border border-border bg-card">{t4}{t5}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
function _temp(v) {
  return !v;
}
function StatRow(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-muted-foreground">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <span className="font-mono text-foreground">{value}</span>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center justify-between py-0.5 text-sm">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function IssueSeverityIcon(t0) {
  const $ = _c(3);
  const {
    severity
  } = t0;
  if (severity === "critical") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <XCircleIcon className="mt-0.5 size-4 shrink-0 text-destructive" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (severity === "warning") {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <TriangleAlertIcon className="mt-0.5 size-4 shrink-0 text-amber-500" />;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <InfoIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
const SEVERITY_BORDER: Record<PerformanceIssueSeverity, string> = {
  critical: 'border-destructive/40 bg-destructive/5',
  warning: 'border-amber-500/40 bg-amber-500/5',
  info: 'border-border bg-muted/30'
};
const CATEGORY_BADGE: Record<PerformanceIssueCategory, string> = {
  timing: 'bg-violet-500/15 text-violet-400',
  render: 'bg-blue-500/15 text-blue-400',
  simulation: 'bg-teal-500/15 text-teal-400',
  physics: 'bg-orange-500/15 text-orange-400',
  network: 'bg-pink-500/15 text-pink-400',
  memory: 'bg-yellow-500/15 text-yellow-400'
};
interface GpuMemoryRow {
  label: string;
  bucket: GpuMemoryBucket | undefined;
}
function GpuMemoryTable(t0) {
  const $ = _c(21);
  const {
    gpu
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== gpu.attributes || $[1] !== gpu.geometries || $[2] !== gpu.indexAttributes || $[3] !== gpu.indirectStorageAttributes || $[4] !== gpu.programs || $[5] !== gpu.readbackBuffers || $[6] !== gpu.renderTargets || $[7] !== gpu.storageAttributes || $[8] !== gpu.textures) {
    const rows = [{
      label: "Attributes",
      bucket: gpu.attributes
    }, {
      label: "Geometries",
      bucket: gpu.geometries
    }, {
      label: "Index Attributes",
      bucket: gpu.indexAttributes
    }, {
      label: "Indirect Storage Attributes",
      bucket: gpu.indirectStorageAttributes
    }, {
      label: "Programs",
      bucket: gpu.programs
    }, {
      label: "Readback Buffers",
      bucket: gpu.readbackBuffers
    }, {
      label: "Render Targets",
      bucket: gpu.renderTargets
    }, {
      label: "Storage Attributes",
      bucket: gpu.storageAttributes
    }, {
      label: "Textures",
      bucket: gpu.textures
    }].filter(_temp2);
    t3 = "overflow-x-auto";
    t1 = "w-full text-xs";
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <thead><tr className="border-b border-border text-left text-muted-foreground"><th className="pb-1.5 pr-4 font-medium">Resource</th><th className="pb-1.5 pr-4 text-right font-medium">Count</th><th className="pb-1.5 text-right font-medium">Size</th></tr></thead>;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    t4 = <tbody>{rows.map(_temp3)}</tbody>;
    $[0] = gpu.attributes;
    $[1] = gpu.geometries;
    $[2] = gpu.indexAttributes;
    $[3] = gpu.indirectStorageAttributes;
    $[4] = gpu.programs;
    $[5] = gpu.readbackBuffers;
    $[6] = gpu.renderTargets;
    $[7] = gpu.storageAttributes;
    $[8] = gpu.textures;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
  }
  let t5;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t4) {
    t5 = <table className={t1}>{t2}{t4}</table>;
    $[14] = t1;
    $[15] = t2;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t3 || $[19] !== t5) {
    t6 = <div className={t3}>{t5}</div>;
    $[18] = t3;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
}
function _temp3(row_0) {
  return <tr key={row_0.label} className="border-b border-border/50 last:border-0"><td className="py-0.5 pr-4">{row_0.label}</td><td className="py-0.5 pr-4 text-right font-mono">{fmtNum(row_0.bucket.count)}</td><td className="py-0.5 text-right font-mono">{formatGpuBucketSize(row_0.bucket)}</td></tr>;
}
function _temp2(row) {
  return row.bucket !== undefined;
}
GpuMemoryTable.displayName = 'GpuMemoryTable';

// ─── main component ──────────────────────────────────────────────────────────

interface PerfSnapshotViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export const PerfSnapshotViewerDialog = ({
  open,
  onOpenChange
}: PerfSnapshotViewerDialogProps) => {
  const [snapshot, setSnapshot] = useState<PerformanceSnapshotV1 | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showAllScopes, setShowAllScopes] = useState(false);
  const [tickOverrideOpen, setTickOverrideOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setSnapshot(null);
      setError(null);
      setIsDragging(false);
      setShowAllScopes(false);
      setTickOverrideOpen(false);
    }
  }, [open]);
  const loadFile = useCallback((file: File) => {
    if (!file.name.endsWith('.json')) {
      setError('Please select a JSON file (gnsx-perf-*.json).');
      return;
    }
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const parsed = JSON.parse(e.target?.result as string) as unknown;
        if (typeof parsed !== 'object' || parsed === null || (parsed as Record<string, unknown>).schemaVersion !== 1) {
          setError('Not a valid Genesys performance snapshot (schemaVersion must be 1).');
          return;
        }
        setError(null);
        setSnapshot(parsed as PerformanceSnapshotV1);
      } catch {
        setError('Failed to parse JSON file.');
      }
    };
    reader.readAsText(file);
  }, []);
  const handleFileChange = useCallback((e_0: React.ChangeEvent<HTMLInputElement>) => {
    const file_0 = e_0.target.files?.[0];
    if (file_0) {
      loadFile(file_0);
    }
    // Reset input so the same file can be re-opened
    e_0.target.value = '';
  }, [loadFile]);
  const handleDrop = useCallback((e_1: React.DragEvent) => {
    e_1.preventDefault();
    setIsDragging(false);
    const file_1 = e_1.dataTransfer.files[0];
    if (file_1) {
      loadFile(file_1);
    }
  }, [loadFile]);
  const handleDragOver = useCallback((e_2: React.DragEvent) => {
    e_2.preventDefault();
    setIsDragging(true);
  }, []);
  const handleDragLeave = useCallback(() => setIsDragging(false), []);
  const scopes = snapshot?.timing?.scopes ?? [];
  const hasExcl = scopes.some(s => s.selfAvg !== undefined);
  const hasCpf = scopes.some(s_0 => s_0.callsPerFrame !== undefined);
  const visibleScopes = showAllScopes ? scopes : scopes.slice(0, SCOPES_PAGE);
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] min-w-4xl max-w-4xl flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <div className="flex items-start justify-between gap-4 pr-10">
            <div className="flex min-w-0 items-center gap-2">
              <ActivityIcon className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <DialogTitle>Perf Snapshot Report</DialogTitle>
                {snapshot && <p className="mt-0.5 text-xs text-muted-foreground">
                    {new Date(snapshot.capturedAt).toLocaleString()}
                  </p>}
              </div>
            </div>
            {snapshot && <Button variant="outline" size="sm" className="shrink-0" onClick={() => fileInputRef.current?.click()}>
                <UploadIcon className="mr-1.5 size-3.5" />
                Open Another
              </Button>}
          </div>
        </DialogHeader>

        <input ref={fileInputRef} type="file" accept=".json" className="hidden" onChange={handleFileChange} />

        <div className="min-h-0 flex-1 overflow-y-auto">
          {/* ── empty state ── */}
          {!snapshot && <div className={`flex h-64 cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed transition-colors ${isDragging ? 'border-primary bg-primary/5' : 'border-border'}`} onClick={() => fileInputRef.current?.click()} onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
              <UploadIcon className="size-10 text-muted-foreground" />
              <div className="text-center">
                <p className="text-sm font-medium">Drop a gnsx-perf-*.json file here</p>
                <p className="text-xs text-muted-foreground">or click to browse</p>
              </div>
              {error && <p className="flex items-center gap-1.5 text-xs text-destructive">
                  <AlertTriangleIcon className="size-3.5" />
                  {error}
                </p>}
            </div>}

          {/* ── report ── */}
          {snapshot && <div className="space-y-4 pb-4">
              {/* Issues */}
              {snapshot.issues.length > 0 && <SectionCard title={`Issues (${snapshot.issues.length})`}>
                  <div className="space-y-2">
                    {snapshot.issues.map(issue => <div key={issue.id} className={`rounded-md border p-3 ${SEVERITY_BORDER[issue.severity]}`}>
                        <div className="flex items-start gap-2">
                          <IssueSeverityIcon severity={issue.severity} />
                          <div className="min-w-0 flex-1">
                            <div className="mb-1 flex flex-wrap items-center gap-1.5">
                              <span className="text-sm font-medium text-foreground">{issue.message}</span>
                              <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${CATEGORY_BADGE[issue.category]}`}>
                                {issue.category}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground">{issue.suggestion}</p>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </SectionCard>}

              {/* Overview */}
              <SectionCard title="Overview">
                <div className="grid grid-cols-2 gap-x-8 gap-y-0.5 sm:grid-cols-3">
                  {snapshot.meta.gameId && <StatRow label="Game ID" value={snapshot.meta.gameId} />}
                  {snapshot.meta.mapName && <StatRow label="Map" value={snapshot.meta.mapName} />}
                  <StatRow label="Renderer" value={snapshot.meta.renderer} />
                  <StatRow label="Play State" value={snapshot.meta.playState} />
                  {snapshot.meta.fps !== undefined && <StatRow label="FPS" value={`~${snapshot.meta.fps}`} />}
                </div>
              </SectionCard>

              {/* GPU Memory */}
              {(snapshot.memory.gpu || snapshot.memory.jsHeapUsedMB !== undefined) && <CollapsibleSectionCard title={formatGpuMemoryTitle(snapshot.memory)} defaultOpen={false}>
                  {snapshot.memory.gpu?.totalBytes !== undefined && <StatRow label="Total GPU Memory" value={formatBytes(snapshot.memory.gpu.totalBytes)} />}
                  {snapshot.memory.jsHeapUsedMB !== undefined && <StatRow label="JS Heap (Chrome)" value={`${snapshot.memory.jsHeapUsedMB} MB`} />}
                  {snapshot.memory.gpu && <div className={snapshot.memory.gpu.totalBytes !== undefined || snapshot.memory.jsHeapUsedMB !== undefined ? 'mt-3' : ''}>
                      <GpuMemoryTable gpu={snapshot.memory.gpu} />
                    </div>}
                </CollapsibleSectionCard>}

              {/* Frame Timing */}
              {snapshot.timing?.frameStats && <SectionCard title="Frame Timing">
                  <div className="grid grid-cols-2 gap-x-8 sm:grid-cols-3">
                    <StatRow label="Samples" value={fmtNum(snapshot.timing.frameStats.frameCount)} />
                    <StatRow label="Avg" value={ms(snapshot.timing.frameStats.avgMs)} />
                    <StatRow label="P95" value={ms(snapshot.timing.frameStats.p95Ms)} />
                    <StatRow label="Min" value={ms(snapshot.timing.frameStats.minMs)} />
                    <StatRow label="Max" value={ms(snapshot.timing.frameStats.maxMs)} />
                    <StatRow label="Slow frames (>16.7 ms)" value={`${snapshot.timing.frameStats.slowFrames} / ${snapshot.timing.frameStats.frameCount}`} />
                  </div>
                </SectionCard>}

              {/* Render */}
              <SectionCard title="Render">
                <div className="grid grid-cols-2 gap-x-8 sm:grid-cols-3">
                  <StatRow label="Draw Calls" value={fmtNum(snapshot.render.drawCalls)} />
                  <StatRow label="Triangles" value={fmtNum(snapshot.render.triangles)} />
                  <StatRow label="Geometries" value={fmtNum(snapshot.render.geometries)} />
                  <StatRow label="Textures" value={fmtNum(snapshot.render.textures)} />
                  {snapshot.render.programs !== undefined && <StatRow label="Shader Programs" value={fmtNum(snapshot.render.programs)} />}
                </div>
              </SectionCard>

              {/* Simulation */}
              {snapshot.simulation && <SectionCard title="Simulation">
                  <div className="grid grid-cols-2 gap-x-8 sm:grid-cols-3">
                    {snapshot.simulation.physics && <>
                        <StatRow label="Physics Bodies" value={fmtNum(snapshot.simulation.physics.bodies)} />
                        <StatRow label="Colliders" value={fmtNum(snapshot.simulation.physics.colliders)} />
                        <StatRow label="Vehicles" value={fmtNum(snapshot.simulation.physics.vehicles)} />
                        {snapshot.simulation.physics.characterControllers !== undefined && <StatRow label="Character Controllers" value={fmtNum(snapshot.simulation.physics.characterControllers)} />}
                        {snapshot.simulation.physics.collisionEventsLastFrame !== undefined && <StatRow label="Collision Events/Frame" value={fmtNum(snapshot.simulation.physics.collisionEventsLastFrame)} />}
                      </>}
                    {snapshot.simulation.timers && <StatRow label="Active Timers" value={fmtNum(snapshot.simulation.timers.activeTimers)} />}
                    {snapshot.simulation.tweens && <StatRow label="Active Tweens" value={fmtNum(snapshot.simulation.tweens.activeTweens)} />}
                    {snapshot.simulation.idleTickerCount !== undefined && <StatRow label="Idle Tickers" value={fmtNum(snapshot.simulation.idleTickerCount)} />}
                    {snapshot.simulation.tickAudit && <StatRow label="Actors Ticking" value={fmtNum(snapshot.simulation.tickAudit.actorsTicking)} />}
                  </div>
                  {(snapshot.simulation.tickAudit?.componentsWithTickOverride?.length ?? 0) > 0 && <div className="mt-3">
                      <button type="button" className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground" onClick={() => setTickOverrideOpen(v => !v)}>
                        {tickOverrideOpen ? <ChevronUpIcon className="size-3.5" /> : <ChevronDownIcon className="size-3.5" />}
                        Components With Tick Override ({snapshot.simulation.tickAudit?.componentsWithTickOverride.length})
                      </button>
                      {tickOverrideOpen && <div className="mt-1.5 overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-border text-left text-muted-foreground">
                                <th className="pb-1.5 pr-4 font-medium">Component</th>
                                <th className="pb-1.5 text-right font-medium">Count</th>
                              </tr>
                            </thead>
                            <tbody>
                              {snapshot.simulation.tickAudit?.componentsWithTickOverride.map(row => <tr key={row.className} className="border-b border-border/50 last:border-0">
                                  <td className="py-0.5 pr-4 font-mono">{row.className}</td>
                                  <td className="py-0.5 text-right font-mono">{fmtNum(row.count)}</td>
                                </tr>)}
                            </tbody>
                          </table>
                        </div>}
                    </div>}
                </SectionCard>}

              {/* CPU Scopes Table */}
              {scopes.length > 0 && <CollapsibleSectionCard title={`CPU Scopes (${scopes.length} top entries, sorted by incl avg)`} defaultOpen={false}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-border text-left text-muted-foreground">
                          <th className="min-w-[12rem] pb-2 pr-4 font-medium">Label</th>
                          <th className="w-20 pb-2 pr-2 text-right font-medium whitespace-nowrap cursor-help" title="Inclusive average — mean wall-clock time per call including all child scopes. High values may reflect orchestration cost rather than actual work.">Incl Avg</th>
                          <th className="w-20 pb-2 pr-2 text-right font-medium whitespace-nowrap cursor-help" title="Inclusive 95th percentile — 95% of calls to this scope completed within this time. Useful for spotting occasional spikes.">Incl P95</th>
                          {hasExcl && <th className="w-20 pb-2 pr-2 text-right font-medium whitespace-nowrap cursor-help" title="Exclusive (self) average — mean time spent inside this scope only, excluding child scopes. This is the most accurate indicator of where actual CPU work happens.">Excl Avg</th>}
                          {hasExcl && <th className="w-20 pb-2 pr-2 text-right font-medium whitespace-nowrap cursor-help" title="Exclusive 95th percentile — 95% of calls completed their own (non-child) work within this time.">Excl P95</th>}
                          {hasCpf && <th className="w-16 pb-2 pr-2 text-right font-medium whitespace-nowrap cursor-help" title="Average number of times this scope is called per frame. Values above 1 mean the same scope runs multiple times per frame (e.g. once per render pass).">Calls/Fr</th>}
                          {hasCpf && <th className="w-20 pb-2 text-right font-medium whitespace-nowrap cursor-help" title="Excl Avg × Calls/Fr — estimated exclusive CPU time this scope consumes per frame in total. Uses self time to avoid double-counting child scope work; values are additive across rows.">Total/Fr</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {visibleScopes.map(scope => {
                    // Use exclusive (self) avg × callsPerFrame for Total/Fr.
                    // Self time excludes child scopes, making this metric additive across
                    // the table and preventing double-counting from nested profiler spans.
                    // Falls back to inclusive avg only when self time is unavailable.
                    const selfPerCall = scope.selfAvg ?? scope.avg;
                    const totalPerFrame = scope.callsPerFrame !== undefined ? scope.callsPerFrame * selfPerCall : undefined;
                    return <tr key={scope.label} className={`border-b border-border/50 last:border-0 ${scope.avg > SCOPE_SLOW_MS ? 'bg-destructive/5' : ''}`}>
                              <td className={`py-1 pr-4 font-mono ${scope.avg > SCOPE_SLOW_MS ? 'text-destructive' : ''}`}>{scope.label}</td>
                              <td className="py-1 pr-2 text-right font-mono whitespace-nowrap">{ms(scope.avg)}</td>
                              <td className="py-1 pr-2 text-right font-mono whitespace-nowrap">{ms(scope.p95)}</td>
                              {hasExcl && <td className="py-1 pr-2 text-right font-mono whitespace-nowrap">
                                  {scope.selfAvg !== undefined ? ms(scope.selfAvg) : <span className="text-muted-foreground">n/a</span>}
                                </td>}
                              {hasExcl && <td className="py-1 pr-2 text-right font-mono whitespace-nowrap">
                                  {scope.selfP95 !== undefined ? ms(scope.selfP95) : <span className="text-muted-foreground">n/a</span>}
                                </td>}
                              {hasCpf && <td className="py-1 pr-2 text-right font-mono whitespace-nowrap">
                                  {scope.callsPerFrame !== undefined ? scope.callsPerFrame.toFixed(1) : <span className="text-muted-foreground">n/a</span>}
                                </td>}
                              {hasCpf && <td className="py-1 text-right font-mono whitespace-nowrap">
                                  {totalPerFrame !== undefined ? ms(totalPerFrame) : <span className="text-muted-foreground">n/a</span>}
                                </td>}
                            </tr>;
                  })}
                      </tbody>
                    </table>
                  </div>
                  {scopes.length > SCOPES_PAGE && <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={() => setShowAllScopes(v_0 => !v_0)}>
                      {showAllScopes ? <><ChevronUpIcon className="mr-1.5 size-3.5" /> Show Less</> : <><ChevronDownIcon className="mr-1.5 size-3.5" /> Show All {scopes.length} Scopes</>}
                    </Button>}
                </CollapsibleSectionCard>}

              {/* World */}
              <CollapsibleSectionCard title={`World — ${fmtNum(snapshot.world.actorCount)} actors`} defaultOpen={false}>
                <StatRow label="Total Actors" value={fmtNum(snapshot.world.actorCount)} />
                {snapshot.world.actorsByClass.length > 0 && <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-border text-left text-muted-foreground">
                          <th className="pb-1.5 pr-4 font-medium">Class</th>
                          <th className="pb-1.5 text-right font-medium">Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {snapshot.world.actorsByClass.map(row_0 => <tr key={row_0.className} className="border-b border-border/50 last:border-0">
                            <td className="py-0.5 pr-4 font-mono">{row_0.className}</td>
                            <td className="py-0.5 text-right font-mono">{fmtNum(row_0.count)}</td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>}
              </CollapsibleSectionCard>

              {/* Network */}
              {snapshot.network && <SectionCard title="Network">
                  <div className="grid grid-cols-2 gap-x-8 sm:grid-cols-3">
                    <StatRow label="Local Objects" value={fmtNum(snapshot.network.numLocalObjects)} />
                    <StatRow label="Remote Objects" value={fmtNum(snapshot.network.numRemoteObjects)} />
                    <StatRow label="Clients in Session" value={fmtNum(snapshot.network.numClientsInSession)} />
                    <StatRow label="Sent" value={`${(snapshot.network.bytesSentPerSecond / 1024).toFixed(1)} KB/s`} />
                    <StatRow label="Received" value={`${(snapshot.network.bytesReceivedPerSecond / 1024).toFixed(1)} KB/s`} />
                    {snapshot.network.serverRttMs !== null && snapshot.network.serverRttMs !== undefined && <StatRow label="Server RTT" value={ms(snapshot.network.serverRttMs)} />}
                  </div>
                </SectionCard>}
            </div>}
        </div>
      </DialogContent>
    </Dialog>;
};
PerfSnapshotViewerDialog.displayName = 'PerfSnapshotViewerDialog';